import React from 'react';
import Paper from '@material-ui/core/Paper';
import TableBody from '@material-ui/core/TableBody';
import Table from '@material-ui/core/Table';
import TablePagination from '@material-ui/core/TablePagination';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import { createStyles, withStyles, WithStyles, Theme } from '@material-ui/core/styles';
import classNames from 'classnames';

import Title from '../../Common/Title';
import locales from '../../../locales';
import StatisticReserveTableHead from './StatisticReserveTableHead';
import StatisticsReserveTableItem from './StatisticsReserveTableItem';
import { IBookingOrders, TPages } from '../../../types/IStatisticStore';
import { TableContainer } from "@material-ui/core";
import ReserveMobileCard from './ReserveMobileCard';

interface Props extends WithStyles<typeof styles> {
  bookingsOrders: IBookingOrders;
  reserves: Array<any>;
  pages: TPages;
  handleGetReserves: (page: number, size: number, sortType: 'desc' | 'asc') => void;
  handleOpenBookingDetails: (bookingId: number) => void;
  moneySymbol: string;
}

const styles = (theme: Theme) => createStyles({
  // Десктопная версия
  desktopPaper: {
    [theme.breakpoints.down(960)]: {
      display: 'none',
    },
  },
  // Мобильная версия
  mobileCard: {
    display: 'none',
    [theme.breakpoints.down(960)]: {
      display: 'block',
    },
  },
  card: {
    borderRadius: 8,
    border: '1px solid #E5E5E5',
    width: '100%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  cardContent: {
    padding: 0,
    width: '100%',
    boxSizing: 'border-box',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  title: {
    fontSize: 20,
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: 0,
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(1),
  },
  reservesContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  pagination: {
    padding: theme.spacing(1, 2),
    [theme.breakpoints.down(960)]: {
      display: 'none',
    },
  },
  mobilePagination: {
    display: 'none',
    padding: theme.spacing(1, 2),
    [theme.breakpoints.down(960)]: {
      display: 'block',
    },
  },
});

const index: React.FC<Props> = (props) => {
  const {
    bookingsOrders,
    reserves,
    pages,
    handleOpenBookingDetails,
    handleGetReserves,
    moneySymbol,
    classes,
  } = props;

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [sortType, changeSortType] = React.useState<"desc" | "asc">('desc');

  /** @description Ручка для переключение страниц в таблице */
  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage);
    handleGetReserves(newPage, rowsPerPage, sortType);
  };

  /** @description Ручка для переключение количества строк в таблице */
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
    handleGetReserves(0, +event.target.value, sortType);
  };

  /** @description Ручка для изменения типа сортировки */
  const handleChangeSortType = () => {
    if (sortType === 'desc') {
      changeSortType('asc');
      handleGetReserves(page, rowsPerPage, 'asc');
    } else {
      changeSortType('desc');
      handleGetReserves(page, rowsPerPage, 'desc');
    }
  };

  return (
    <>
      {/* Десктопная версия (таблица) */}
      <Paper elevation={3} className={classes.desktopPaper}>
        <Title
          padding="8px 12px 8px 24px"
          text={locales.t('statistics.reserves.title')}
          borderBottom
          spaceBetween
        />
        <TableContainer>
          <Table>
            <StatisticReserveTableHead
              onRequestSort={handleChangeSortType}
              order={sortType}
              orderBy="date"
            />
            <TableBody>
              {
                reserves.map(reserve => (
                  <StatisticsReserveTableItem
                    key={reserve.id}
                    order={bookingsOrders[reserve.id]}
                    reserveInfo={reserve}
                    handleOpenBookingDetails={handleOpenBookingDetails}
                    moneySymbol={moneySymbol}
                  />
                ))
              }
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          className={classes.pagination}
          labelRowsPerPage="Строк на странице:"
          style={{ paddingRight: 8 }}
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={pages.totalElements}
          rowsPerPage={pages.size || rowsPerPage}
          page={page}
          backIconButtonProps={{
            'aria-label': 'previous page',
          }}
          nextIconButtonProps={{
            'aria-label': 'next page',
          }}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Мобильная версия (карточки) */}
      <Card className={classNames(classes.mobileCard)} elevation={0}>
        <CardContent className={classes.cardContent}>
          <Typography className={classes.title}>
            {locales.t('statistics.reserves.title')}
          </Typography>
          <div className={classes.reservesContainer}>
            {
              reserves.map(reserve => (
                <ReserveMobileCard
                  key={reserve.id}
                  order={bookingsOrders[reserve.id]}
                  reserveInfo={reserve}
                  handleOpenBookingDetails={handleOpenBookingDetails}
                  moneySymbol={moneySymbol}
                />
              ))
            }
          </div>
          <TablePagination
            className={classes.mobilePagination}
            labelRowsPerPage="Строк на странице:"
            style={{ paddingRight: 8 }}
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={pages.totalElements}
            rowsPerPage={pages.size || rowsPerPage}
            page={page}
            backIconButtonProps={{
              'aria-label': 'previous page',
            }}
            nextIconButtonProps={{
              'aria-label': 'next page',
            }}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>
    </>
  );
};

export default withStyles(styles)(index);
